/**
 * Wizard step 2, Picture (W2-14 onboarding-wizard; DESIGN.md §4.3 "(2) PICTURE", §5.1 step 3, R§1.3, P§2.7).
 *
 * Drop or choose an image. Two previews show it as Phira will:
 *  - "In the game": the io worker's Phira-exact blur (3 box passes of width 101, edge-clamped) drawn crop-centre
 *    into a 16:9 frame, the 0.3 black layer over the whole window and the background dim (0.6, info.yml default)
 *    over the chart viewport, with a judge line where a new chart's first line sits;
 *  - "Song select": the sharp picture cropped to 16:9.
 * Pictures over 4096 px get a downscale offer (a 4096 px copy, re-encoded by the io worker); formats Phira cannot
 * decode (AVIF, HEIC, ...) must be converted to PNG first (Next is disabled until then). Without a picture the
 * previews show the gradient placeholder generated from the title, which Create writes as `illustration.png` and
 * Problems flags (ST-02).
 */

import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent as ReactDragEvent } from 'react';
import type { NewChartDraft, WizardStepProps } from '../../../app/registry/types';
import { Button, Icon, ProgressBar } from '../../../ui';
import { baseName, cleanText, stem } from '../draft';
import { metaOf, sniffFile } from '../songAnalysis';
import { m } from '../wizard.messages';
import s from '../Wizard.module.css';
import { useServicesText, useStepActions } from '../wizardSession';
import { fileBytes } from './SongStep';

const IMAGE_ACCEPT = 'image/*,.png,.jpg,.jpeg,.webp,.bmp,.gif,.avif';
/** Pictures larger than this (either side) get the downscale offer. */
export const MAX_PICTURE_SIDE = 4096;
/** Working size of the previews (longest side, px). */
const PREVIEW_SIDE = 640;
/** Phira's background layers (R§1.3): black 0.3 over the window, then the dim inside the viewport. */
const PHIRA_WINDOW_SHADE = 0.3;
const PHIRA_DEFAULT_DIM = 0.6;
/** Screen height fraction of a new chart's first line (RPE y = -250 of the 900 px tall field). */
const FIRST_LINE_Y = 0.5 + 250 / 900;

type Busy = 'reading' | 'downscaling' | 'converting' | null;

interface PreviewImages {
  readonly blurred: ImageBitmap | null;
  readonly sharp: ImageBitmap | null;
}

/** Previews drawn for one picture (or for the placeholder of one title). */
interface PreviewResult {
  readonly pic: NewChartDraft['illustration'];
  readonly title: string;
  readonly images: PreviewImages;
}

function cssVar(el: Element, name: string): string {
  const v = getComputedStyle(el).getPropertyValue(name).trim();
  return v === '' ? getComputedStyle(el).color : v;
}

function sizeCanvas(el: HTMLCanvasElement): { w: number; h: number } {
  const dpr = typeof devicePixelRatio === 'number' && devicePixelRatio > 0 ? devicePixelRatio : 1;
  const w = Math.max(1, Math.round(el.clientWidth * dpr));
  const h = Math.max(1, Math.round(el.clientHeight * dpr));
  if (el.width !== w) el.width = w;
  if (el.height !== h) el.height = h;
  return { w, h };
}

/** Crop-centre fit (scale to cover, centre, crop the overflow), as Phira draws the background. */
function drawCover(ctx: CanvasRenderingContext2D, img: ImageBitmap, w: number, h: number): void {
  const scale = Math.max(w / img.width, h / img.height);
  const dw = img.width * scale;
  const dh = img.height * scale;
  ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
}

function drawGame(el: HTMLCanvasElement, img: ImageBitmap): void {
  const ctx = el.getContext('2d');
  if (ctx === null) return;
  const { w, h } = sizeCanvas(el);
  ctx.globalAlpha = 1;
  ctx.fillStyle = 'rgb(0 0 0)';
  ctx.fillRect(0, 0, w, h);
  drawCover(ctx, img, w, h);
  // Phira's two black layers (exact game semantics, not UI colours).
  ctx.fillStyle = `rgb(0 0 0 / ${String(PHIRA_WINDOW_SHADE)})`;
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = `rgb(0 0 0 / ${String(PHIRA_DEFAULT_DIM)})`;
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = cssVar(el, '--color-text');
  const thickness = Math.max(1, Math.round(h * 0.008));
  ctx.fillRect(w * 0.15, h * FIRST_LINE_Y - thickness / 2, w * 0.7, thickness);
}

function drawSelect(el: HTMLCanvasElement, img: ImageBitmap): void {
  const ctx = el.getContext('2d');
  if (ctx === null) return;
  const { w, h } = sizeCanvas(el);
  ctx.clearRect(0, 0, w, h);
  drawCover(ctx, img, w, h);
}

export function PictureStep(props: WizardStepProps) {
  const { services, draft } = props;
  const { update, setCanContinue } = useStepActions(props);
  const text = useServicesText(services);
  const input = useRef<HTMLInputElement>(null);
  const gameCanvas = useRef<HTMLCanvasElement>(null);
  const selectCanvas = useRef<HTMLCanvasElement>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState<PreviewResult | null>(null);

  const pic = draft.illustration;
  const sniff = useMemo(() => (pic === null ? null : sniffFile(pic.bytes, pic.name)), [pic]);
  const meta = useMemo(() => (pic === null ? null : metaOf(pic.bytes, pic.name)), [pic]);
  const decodable = sniff === null || sniff.phiraDecodable;
  const width = meta?.width ?? 0;
  const height = meta?.height ?? 0;
  const large = Math.max(width, height) > MAX_PICTURE_SIDE;
  const title = cleanText(draft.name) === '' ? text.t(m.untitled) : cleanText(draft.name);
  const previewCurrent = preview !== null && preview.pic === pic && (pic !== null || preview.title === title);
  const previewState: 'busy' | 'ready' | 'failed' = !previewCurrent
    ? 'busy'
    : preview.images.blurred === null && preview.images.sharp === null
      ? 'failed'
      : 'ready';

  useEffect(() => {
    setCanContinue(busy === null && decodable);
  }, [busy, decodable, setCanContinue]);

  // Preview images: the chosen picture, or the placeholder for the current title.
  useEffect(() => {
    let cancelled = false;
    const run = async (): Promise<PreviewImages> => {
      const bytes =
        pic !== null && decodable
          ? pic.bytes
          : pic === null
            ? await services.io.call('placeholderIllustration', {
                title,
                width: PREVIEW_SIDE,
                height: (PREVIEW_SIDE * 9) / 16,
              })
            : null;
      if (bytes === null) return { blurred: null, sharp: null };
      const [blurred, sharp] = await Promise.all([
        services.io.call('blurIllustration', { bytes, maxSide: PREVIEW_SIDE }).catch(() => null),
        createImageBitmap(new Blob([bytes as Uint8Array<ArrayBuffer>], { type: pic?.mime ?? 'image/png' })).catch(
          () => null,
        ),
      ]);
      return { blurred, sharp };
    };
    const timer = setTimeout(
      () => {
        run().then(
          (images) => {
            if (cancelled) {
              images.blurred?.close();
              images.sharp?.close();
              return;
            }
            setPreview({ pic, title, images });
          },
          () => {
            if (!cancelled) setPreview({ pic, title, images: { blurred: null, sharp: null } });
          },
        );
      },
      pic === null ? 250 : 0,
    );
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [pic, decodable, title, services]);

  useEffect(() => {
    if (preview === null) return undefined;
    const { images } = preview;
    const g = gameCanvas.current;
    const sel = selectCanvas.current;
    const blurred = images.blurred ?? images.sharp;
    if (g !== null && blurred !== null) drawGame(g, blurred);
    if (sel !== null && images.sharp !== null) drawSelect(sel, images.sharp);
    // Replaced or unmounted: the bitmaps are no longer drawn.
    return () => {
      images.blurred?.close();
      images.sharp?.close();
    };
  }, [preview]);

  const choose = useCallback(
    async (file: File | null): Promise<void> => {
      if (file === null) return;
      setError(null);
      setBusy('reading');
      try {
        const bytes = await fileBytes(file);
        const sn = sniffFile(bytes, file.name);
        if (sn.kind !== 'image') {
          setError(text.t(m.picNotImage, { name: file.name }));
          return;
        }
        update({ illustration: { bytes, name: baseName(file.name), mime: sn.mime, placeholder: false } });
      } catch (e) {
        setError(text.t(m.picError, { detail: e instanceof Error ? e.message : String(e) }));
      } finally {
        setBusy(null);
      }
    },
    [text, update],
  );

  /** Re-encodes the picture through the io worker (downscale or format conversion). */
  const reencode = async (kind: 'downscaling' | 'converting'): Promise<void> => {
    if (pic === null || busy !== null) return;
    setBusy(kind);
    setError(null);
    try {
      const jpeg = kind === 'downscaling' && sniff?.codec === 'jpg';
      const maxSide = kind === 'downscaling' ? MAX_PICTURE_SIDE : Math.max(width, height, 1);
      const bytes = await services.io.call('downscaleImage', {
        bytes: pic.bytes,
        maxSide: kind === 'converting' && maxSide <= 1 ? MAX_PICTURE_SIDE : maxSide,
        mime: jpeg ? 'image/jpeg' : 'image/png',
      });
      update({
        illustration: {
          bytes,
          name: `${stem(pic.name)}.${jpeg ? 'jpg' : 'png'}`,
          mime: jpeg ? 'image/jpeg' : 'image/png',
          placeholder: false,
        },
      });
    } catch (e) {
      setError(text.t(m.picError, { detail: e instanceof Error ? e.message : String(e) }));
    } finally {
      setBusy(null);
    }
  };

  const onDrop = (e: ReactDragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const list = e.dataTransfer.files;
    void choose(list.length > 0 ? (list[0] ?? null) : null);
  };

  return (
    <div className={s.step}>
      <div
        className={s.dropZone}
        data-over={dragOver ? 'true' : undefined}
        // Compact once a picture is chosen (room for its facts); on short screens also before (room for the previews).
        data-compact={pic !== null ? 'true' : 'short'}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = 'copy';
          setDragOver(true);
        }}
        onDragLeave={() => {
          setDragOver(false);
        }}
        onDrop={onDrop}
      >
        <Icon name="Image" size={28} />
        <p className={s.dropText}>
          <span>{text.t(m.picDrop)}</span>{' '}
          <Button
            variant={pic === null ? 'primary' : 'secondary'}
            icon="FolderOpen"
            onClick={() => {
              input.current?.click();
            }}
          >
            {text.t(pic === null ? m.chooseFile : m.chooseAnother)}
          </Button>
        </p>
        <p className={s.hint}>{text.t(m.picAccepted)}</p>
        <input
          ref={input}
          type="file"
          accept={IMAGE_ACCEPT}
          className={s.hiddenInput}
          tabIndex={-1}
          aria-hidden="true"
          data-testid="picture-input"
          onChange={(e) => {
            const list = e.currentTarget.files;
            const file = list !== null && list.length > 0 ? (list[0] ?? null) : null;
            e.currentTarget.value = '';
            void choose(file);
          }}
        />
      </div>

      {busy === 'reading' && <ProgressBar value={null} label={{ key: m.picLoading }} />}
      {busy === 'downscaling' && <ProgressBar value={null} label={{ key: m.picDownscaling }} />}
      {busy === 'converting' && <ProgressBar value={null} label={{ key: m.picConverting }} />}
      {error !== null && (
        <p className={s.error} role="alert">
          <Icon name="CircleX" size={16} />
          <span>{error}</span>
        </p>
      )}

      {pic !== null && sniff !== null && (
        <div className={s.songHeader}>
          <Icon name="FileImage" size={18} />
          <span className={s.songName}>{pic.name}</span>
          {width > 0 && (
            <span className={s.songFacts}>
              {text.t(m.picFacts, { w: width, h: height, codec: sniff.codec.toUpperCase() })}
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            icon="X"
            onClick={() => {
              update({ illustration: null });
            }}
          >
            {text.t(m.picRemove)}
          </Button>
        </div>
      )}

      {pic !== null && !decodable && (
        <div className={s.verdictBlock}>
          <p className={s.verdict} data-kind="warning" role="alert">
            <Icon name="TriangleAlert" size={16} />
            <span>{text.t(m.picUnsupported)}</span>
          </p>
          <div className={s.actions}>
            <Button
              variant="primary"
              icon="FileImage"
              onClick={() => {
                void reencode('converting');
              }}
              disabledReason={busy !== null ? { key: m.picConverting } : null}
            >
              {text.t(m.picConvert)}
            </Button>
          </div>
        </div>
      )}
      {pic !== null && decodable && large && (
        <div className={s.verdictBlock}>
          <p className={s.verdict} data-kind="info">
            <Icon name="Info" size={16} />
            <span>{text.t(m.picLarge, { w: width, h: height })}</span>
          </p>
          <div className={s.actions}>
            <Button
              icon="Shrink"
              onClick={() => {
                void reencode('downscaling');
              }}
              disabledReason={busy !== null ? { key: m.picDownscaling } : null}
            >
              {text.t(m.picDownscale)}
            </Button>
          </div>
        </div>
      )}
      {pic !== null && decodable && sniff !== null && !sniff.rpeFriendly && (
        <p className={s.verdict} data-kind="info">
          <Icon name="Info" size={16} />
          <span>{text.t(m.picNotForRpe)}</span>
        </p>
      )}
      {pic === null && (
        <p className={s.verdict} data-kind="info">
          <Icon name="Palette" size={16} />
          <span>{text.t(m.picPlaceholder)}</span>
        </p>
      )}

      <div className={s.previews}>
        <figure className={s.previewFigure}>
          <canvas ref={gameCanvas} className={s.previewCanvas} role="img" aria-label={text.t(m.previewGameHint)} />
          <figcaption>
            <strong>{text.t(m.previewGame)}</strong> <span className={s.hint}>{text.t(m.previewGameHint)}</span>
          </figcaption>
        </figure>
        <figure className={s.previewFigure}>
          <canvas ref={selectCanvas} className={s.previewCanvas} role="img" aria-label={text.t(m.previewSelectHint)} />
          <figcaption>
            <strong>{text.t(m.previewSelect)}</strong> <span className={s.hint}>{text.t(m.previewSelectHint)}</span>
          </figcaption>
        </figure>
      </div>
      {previewState === 'busy' && <p className={s.hint}>{text.t(m.previewBusy)}</p>}
      {previewState === 'failed' && <p className={s.hint}>{text.t(m.previewUnavailable)}</p>}
    </div>
  );
}
